export interface HistoryItem {
  id: string;
  title: string;
  originalText: string;
  rewrittenText: string;
  prompt: string;
  createdAt: Date;
  updatedAt: Date;
  aiProvider: 'gemini-free' | 'gemini' | 'claude' | 'gpt';
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  metrics: {
    originalLength: number;
    rewrittenLength: number;
    characterChange: number;
    wordCount: number;
    estimatedReadingTime: number;
  };
  tags?: string[];
  summary?: string;
}

export interface HistorySearchOptions {
  query?: string;
  aiProvider?: string;
  dateFrom?: Date;
  dateTo?: Date;
  tags?: string[];
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'characterChange';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

class HistoryManager {
  private storageKey = 'reloop_history';

  private calculateMetrics(originalText: string, rewrittenText: string): HistoryItem['metrics'] {
    const originalLength = originalText.length;
    const rewrittenLength = rewrittenText.length;
    const characterChange = rewrittenLength - originalLength;
    const wordCount = rewrittenText.split(/\s+/).length;
    const estimatedReadingTime = Math.ceil(wordCount / 200); // 200 words per minute

    return {
      originalLength,
      rewrittenLength,
      characterChange,
      wordCount,
      estimatedReadingTime,
    };
  }

  private generateTitle(text: string): string {
    const firstLine = text.split('\n')[0].trim();
    if (firstLine.length > 50) {
      return firstLine.substring(0, 50) + '...';
    }
    return firstLine || '無題のドキュメント';
  }

  private generateSummary(text: string): string {
    const lines = text.split('\n').filter(line => line.trim().length > 0);
    const firstParagraph = lines.slice(0, 3).join(' ');
    if (firstParagraph.length > 150) {
      return firstParagraph.substring(0, 150) + '...';
    }
    return firstParagraph;
  }

  save(
    originalText: string,
    rewrittenText: string,
    prompt: string,
    aiProvider: HistoryItem['aiProvider'],
    tokenUsage?: HistoryItem['tokenUsage'],
    tags?: string[]
  ): HistoryItem {
    const now = new Date();
    const id = `${now.getTime()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const historyItem: HistoryItem = {
      id,
      title: this.generateTitle(rewrittenText),
      originalText,
      rewrittenText,
      prompt,
      createdAt: now,
      updatedAt: now,
      aiProvider,
      tokenUsage,
      metrics: this.calculateMetrics(originalText, rewrittenText),
      tags,
      summary: this.generateSummary(rewrittenText),
    };

    const history = this.getAll();
    history.unshift(historyItem);
    
    // 最大1000件まで保存
    if (history.length > 1000) {
      history.splice(1000);
    }

    localStorage.setItem(this.storageKey, JSON.stringify(history));
    return historyItem;
  }

  getAll(): HistoryItem[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];
      
      const history = JSON.parse(stored);
      return history.map((item: any) => ({
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
      }));
    } catch (error) {
      console.error('Error loading history:', error);
      return [];
    }
  }

  getById(id: string): HistoryItem | null {
    const history = this.getAll();
    return history.find(item => item.id === id) || null;
  }

  search(options: HistorySearchOptions): HistoryItem[] {
    let history = this.getAll();

    // テキスト検索
    if (options.query) {
      const query = options.query.toLowerCase();
      history = history.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.originalText.toLowerCase().includes(query) ||
        item.rewrittenText.toLowerCase().includes(query) ||
        item.prompt.toLowerCase().includes(query) ||
        item.summary?.toLowerCase().includes(query)
      );
    }

    // AIプロバイダーフィルタ
    if (options.aiProvider) {
      history = history.filter(item => item.aiProvider === options.aiProvider);
    }

    // 日付フィルタ
    if (options.dateFrom) {
      history = history.filter(item => item.createdAt >= options.dateFrom!);
    }
    if (options.dateTo) {
      history = history.filter(item => item.createdAt <= options.dateTo!);
    }

    // タグフィルタ
    if (options.tags && options.tags.length > 0) {
      history = history.filter(item =>
        options.tags!.some(tag => item.tags?.includes(tag))
      );
    }

    // ソート
    const sortBy = options.sortBy || 'createdAt';
    const sortOrder = options.sortOrder || 'desc';
    
    history.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortBy === 'characterChange') {
        aValue = a.metrics.characterChange;
        bValue = b.metrics.characterChange;
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
      }

      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // 制限
    if (options.limit) {
      history = history.slice(0, options.limit);
    }

    return history;
  }

  update(id: string, updates: Partial<HistoryItem>): HistoryItem | null {
    const history = this.getAll();
    const index = history.findIndex(item => item.id === id);
    
    if (index === -1) return null;

    history[index] = {
      ...history[index],
      ...updates,
      updatedAt: new Date(),
    };

    localStorage.setItem(this.storageKey, JSON.stringify(history));
    return history[index];
  }

  delete(id: string): boolean {
    const history = this.getAll();
    const filtered = history.filter(item => item.id !== id);
    
    if (filtered.length === history.length) return false;

    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
    return true;
  }

  deleteAll(): void {
    localStorage.removeItem(this.storageKey);
  }

  getStats() {
    const history = this.getAll();
    const totalItems = history.length;
    
    if (totalItems === 0) {
      return {
        totalItems: 0,
        totalCharactersProcessed: 0,
        averageCharacterChange: 0,
        mostUsedProvider: null,
        providerStats: {},
        recentActivity: [],
      };
    }

    const totalCharactersProcessed = history.reduce(
      (sum, item) => sum + item.metrics.originalLength + item.metrics.rewrittenLength,
      0
    );

    const averageCharacterChange = history.reduce(
      (sum, item) => sum + item.metrics.characterChange,
      0
    ) / totalItems;

    const providerCounts = history.reduce((acc, item) => {
      acc[item.aiProvider] = (acc[item.aiProvider] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostUsedProvider = Object.entries(providerCounts).reduce(
      (max, [provider, count]) => 
        count > (max.count || 0) ? { provider, count } : max,
      {} as { provider?: string; count?: number }
    ).provider || null;

    const recentActivity = history.slice(0, 10).map(item => ({
      id: item.id,
      title: item.title,
      createdAt: item.createdAt,
      aiProvider: item.aiProvider,
    }));

    return {
      totalItems,
      totalCharactersProcessed,
      averageCharacterChange: Math.round(averageCharacterChange),
      mostUsedProvider,
      providerStats: providerCounts,
      recentActivity,
    };
  }

  export(): string {
    const history = this.getAll();
    return JSON.stringify(history, null, 2);
  }

  import(jsonData: string): { success: boolean; imported: number; errors: number } {
    try {
      const importedHistory = JSON.parse(jsonData);
      
      if (!Array.isArray(importedHistory)) {
        throw new Error('Invalid format: expected array');
      }

      const existingHistory = this.getAll();
      const existingIds = new Set(existingHistory.map(item => item.id));
      
      let imported = 0;
      let errors = 0;

      const validItems = importedHistory.filter(item => {
        try {
          if (!item.id || !item.title || !item.originalText || !item.rewrittenText) {
            errors++;
            return false;
          }
          
          if (existingIds.has(item.id)) {
            errors++;
            return false;
          }

          imported++;
          return true;
        } catch {
          errors++;
          return false;
        }
      });

      const mergedHistory = [...validItems.map(item => ({
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
      })), ...existingHistory];

      localStorage.setItem(this.storageKey, JSON.stringify(mergedHistory));

      return { success: true, imported, errors };
    } catch (error) {
      console.error('Error importing history:', error);
      return { success: false, imported: 0, errors: 1 };
    }
  }
}

export const historyManager = new HistoryManager();